import React, {Component, ReactNode} from 'react';
import {Dialog} from 'primereact/dialog';
import FileUploader, {UploadProgress} from '../../sandbox/FileUploader';
import AnimatedButton from '../../custom/AnimatedButton';
import LanguageUtil from '../../../util/LanguageUtil';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import {InstanceContainer} from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomListService from '../../../service/CustomListService';
import ServiceContainer from '../../../config/ServiceContainer';
import {ServiceNames} from '../../../constant/ServiceNames';
import MessageUtil from '../../../util/MessageUtil';
import CustomListImportErrorUtil from '../../../util/CustomListImportErrorUtil';
import {CustomListBulkRecordType, CustomListTabType} from '../../../domain/custom-list/CustomListDomains';

interface Props {
  visible: boolean;
  listId: string;
  recordType: CustomListTabType;
  onHide: () => void;
  onSuccess: () => void;
}

class State {
  public file: File | null = null;
  public percent: number = 0;
  public progress: UploadProgress = 'idle';
  public uploading: boolean = false;
}

class CustomListCsvModal extends Component<Props, State> {
  public state: State = new State();

  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);
  private readonly customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);

  public render(): ReactNode {
    const {visible} = this.props;
    const {file, percent, progress, uploading} = this.state;

    return (
      <Dialog
        header={LanguageUtil.get({id: 'pages.customList.csvImport', defaultMessage: 'CSV Import'})}
        className={'min-w-[50vh] min-h-[50vh]'}
        closable={!uploading}
        closeOnEscape={!uploading}
        dismissableMask={!uploading}
        visible={visible}
        onHide={this.handleHide}
      >
        <div className={'flex flex-col gap-y-4 h-full'}>
          <div className={'flex flex-col gap-y-2 items-center justify-center w-full h-full border-2 border-dashed'}>
            <FileUploader
              acceptedFiles={['text/csv']}
              file={file}
              percent={percent}
              progress={progress}
              onChangeFile={(selectedFile) => this.setState({file: selectedFile, progress: 'idle', percent: 0})}
              maxBytes={this.environmentUtil.getStorageMaxSize()}
            />
          </div>
          <div className={'flex justify-end'}>
            <AnimatedButton
              icon={'check'}
              text={LanguageUtil.get({id: 'common.buttons.done', defaultMessage: 'Done'})}
              customType={'primary'}
              disabled={file == null || uploading}
              loading={uploading}
              onClick={this.onDone}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  private handleHide = (): void => {
    if (this.state.uploading) {
      return;
    }
    this.resetState();
    this.props.onHide();
  };

  private onDone = async (): Promise<void> => {
    const {listId, recordType} = this.props;
    const {file} = this.state;
    if (file == null) {
      MessageUtil.error({
        summary: LanguageUtil.get({id: 'pages.customList.fileRequired', defaultMessage: 'Please select a CSV file'}),
      });
      return;
    }

    const type: CustomListBulkRecordType = this.customListService.toBulkRecordType(recordType);
    this.setState({uploading: true, progress: 'uploading', percent: 0});
    try {
      await this.customListService.addBulk(listId, type, file);
      this.setState({uploading: false, progress: 'uploaded', percent: 100});
      MessageUtil.success({
        summary: LanguageUtil.get({id: 'pages.customList.importSuccess', defaultMessage: 'CSV imported successfully'}),
      });
      this.resetState();
      this.props.onSuccess();
      this.props.onHide();
    } catch (e) {
      console.error(e);
      this.setState({uploading: false, progress: 'error'});
      MessageUtil.error({
        summary: CustomListImportErrorUtil.resolveErrorMessage(e, this.props.recordType),
      });
    }
  };

  private resetState = (): void => {
    this.setState(new State());
  };
}

export default CustomListCsvModal;
